/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  CheckCircleIcon, // For GOOD
  ExclamationTriangleIcon, // For WARNING/IMPROVABLE
  XCircleIcon, // For BAD/POOR
  InformationCircleIcon, // Default or other status
  ArrowTopRightOnSquareIcon, // Replaces ExitToAppIcon
  DocumentTextIcon, // Replaces DescriptionIcon
} from "@heroicons/react/24/outline";

import { useQuery, useMutation } from "@tanstack/react-query";
import moment from "moment";

import getPlatformHealthService from "./services/getPlatformHealthService";
import getServicesHealthHistoryByBrandsService, {
  GetServicesHealthHistoryByBrandsVariables,
} from "./services/getServicesHealthHistoryByBrandsService";
import triggerTestsExecutionsService from "./services/triggerTestsExecutionsService";
import getServicesHealthHistoryService, {
  GetServicesHealthHistoryVariables,
} from "./services/getServicesHealthHistoryService";
import { target } from "../../../config/awsExports";
import TestDetailPopup from "./TestDetailPopup";
import { GetPlatformHealthVariables } from "./services/getPlatformHealthService";
import getTestsExecutionsService from "./services/getTestsExecutionsService";
import { GetTestsExecutionsVariables } from "./services/getTestsExecutionsService";
import getProjectsService, {
  Project,
} from "../../../components/ProjectSelector/getProjectsService";
import LoadingAnimation from "../../../components/LoadingAnimation";
import ProjectSelector from "../../../components/ProjectSelector/ProjectSelector";
import ServicesHealthSummary from "./components/ServicesHealthSummary";
import SelectedBrandsChips from "./components/SelectedBrandsChips";
import AllServicesList from "./components/AllServicesList";
import SingleServiceDetailView from "./components/SingleServiceDetailView";
import { useParams, useHistory } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import { useSnackbar } from "../../../providers/SnackbarContext"; // Added import for useSnackbar

const HEALTH_URL =
  "https://eu-west-1.console.aws.amazon.com/cloudwatch/home?region=eu-west-1#alarmsV2:alarm";

export default function PlatformHealth() {
  const { service } = useParams<{ service?: string }>();
  const history = useHistory();

  const [selectedBrand, setSelectedBrand] = useState<string | undefined>();
  const [selectedBrands, setSelectedBrands] = useState<Project[]>([]);
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment());
  const [servicesColumns, setServicesColumns] = useState<any[][]>([]);
  const [, setServicesInBrands] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [healthStatus, setHealthStatus] = useState<number[]>([0, 1, 2]);
  const [pointColors, setPointColors] = useState<string[]>([]);
  const [selectorDates, setSelectorDates] = useState<moment.Moment[]>([]);

  const NUMBER_LAST_DAYS = 8;

  const typedTarget: "DEV" | "PRO" | "STAG" = target; // Explicitly type the constant

  const healthColors: Record<string, string> = {
    GOOD: "green",
    WARNING: "orange",
    IMPROVABLE: "orange",
    BAD: "red",
    POOR: "red",
  };

  const healthLevels: Record<string, number> = {
    GOOD: 0,
    WARNING: 1,
    IMPROVABLE: 1,
    BAD: 2,
    POOR: 2,
  };

  const openSnackBar = useSnackbar();

  const triggerTestsExecutions = useMutation({
    mutationFn: () => triggerTestsExecutionsService({}),
    onSuccess: () => {
      openSnackBar("Tests executions triggered successfully.", "success");
      localStorage.setItem("lastTestExecution", new Date().toISOString());
    },
    onError: (error: any) => {
      openSnackBar(
        `Error triggering tests executions: ${error.message}`,
        "error"
      );
    },
  });

  // Assume these components exist or adapt as needed
  const PageBanner = ({ title }: { title: string }) => (
    <h1 className="text-lg  p-4 bg-[rgba(239,100,34,0.7)] text-white">
      {title}
    </h1>
  );

  // Error message component
  const ErrorMessage = ({ error, onRetry }: { error: any; onRetry?: () => void }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 m-4">
      <div className="flex items-start">
        <XCircleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
        <div className="flex-grow">
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Failed to load platform health data
          </h3>
          <p className="text-red-700 mb-4">
            Unable to fetch data after multiple attempts. Please check your connection and try again.
          </p>
          {error && (
            <details className="mb-4">
              <summary className="text-sm text-red-600 cursor-pointer hover:text-red-800">
                Technical details
              </summary>
              <p className="text-sm text-red-600 mt-2 font-mono bg-red-100 p-2 rounded">
                {error.message || 'Unknown error occurred'}
              </p>
            </details>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Helper to get Heroicons based on health status
  interface GetHealthIconProps {
    health: string | undefined;
  }

  // Update the function to accept a size prop
  const getHealthIcon = ({
    health,
    size = "h-6 w-6",
  }: GetHealthIconProps & { size?: string }) => {
    const iconClass = `${size} mr-2`; // Use the size prop dynamically
    switch (health?.toUpperCase()) {
      case "GOOD":
        return <CheckCircleIcon className={`${iconClass} text-green-500`} />;
      case "WARNING":
      case "IMPROVABLE":
        return (
          <ExclamationTriangleIcon className={`${iconClass} text-orange-500`} />
        );
      case "POOR":
        return <XCircleIcon className={`${iconClass} text-red-500`} />;
      default:
        return (
          <InformationCircleIcon className={`${iconClass} text-gray-500`} />
        );
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event: any, elements: any[]) => {
      console.log("onClick event:", event);
      if (elements.length > 0) {
        const index = elements[0].index;
        const selectedDateFromGraph = labels[index];
        console.log("Selected date from graph:", selectedDateFromGraph);
        setSelectedDate(moment(selectedDateFromGraph));
      }
    },
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        callbacks: {
          label(tooltipItem: { dataIndex: any }) {
            const index = tooltipItem.dataIndex;
            const health = Object.keys(healthLevels).find(
              (key) => healthLevels[key] === healthStatus[index]
            );
            let emoji;
            if (health === "GOOD") {
              emoji = "✅";
            } else if (health === "WARNING" || health === "IMPROVABLE") {
              emoji = "⚠️";
            } else {
              emoji = "❌";
            }
            return `${emoji} ${health}`;
          },
        },
        afterBody: (tooltipItems: any[]) => {
          const index = tooltipItems[0]?.dataIndex;
          if (index !== undefined) {
            const selectedDateFromGraph = labels[index];
            setSelectedDate(moment(selectedDateFromGraph, "DD-MM-YYYY"));
          }
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 30, // Extra space for the x-axis labels
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value: any, index: string | number) =>
            labels[Number(index)], // Use dates from `labels` array for x-axis
          font: {
            size: 12, // Adjust font size for better readability
          },
        },
      },
      y: {
        reverse: true, // Reverse the y-axis
        ticks: {
          callback(value: string | number) {
            if (value === 2) return "❌"; // BAD
            if (value === 1) return "⚠️"; // WARNING
            if (value === 0) return "✅"; // GOOD
            return "";
          },
          stepSize: 1,
          min: 0,
          max: 2,
          font: {
            size: 16, // Adjust font size for emojis
          },
          autoSkip: false, // Ensure all values are shown
          maxTicksLimit: 3, // Ensure all three values are shown
          values: [0, 1, 2],
        },
      },
    },
  } as any;

  const servicesPerColumn = 10;

  const getServicesHealthHistory = async ({
    queryKey,
  }: {
    queryKey: [string, GetServicesHealthHistoryVariables];
  }) => {
    const [, variables] = queryKey as [
      string,
      GetServicesHealthHistoryVariables
    ];

    const { services } = variables;

    const data = await getServicesHealthHistoryService({
      services,
      from: moment().subtract(15, "days").toISOString(),
      to: moment().toISOString(),
    });

    // Transform data immutably
    const transformedData = {
      ...data,
      ServiceHealthHistory: data.ServiceHealthHistory?.map(
        (history: { History: any[] }) => ({
          ...history,
          History: history.History.map((item: { healthPercentage: any }) => ({
            ...item,
            healthPercentage: Math.round(item.healthPercentage || 0), // Ensure healthPercentage is an integer
          })),
        })
      ),
    };

    return transformedData;
  };

  const getServicesHealthHistoryByBrands = async ({
    queryKey,
  }: {
    queryKey: [string, GetServicesHealthHistoryByBrandsVariables];
  }) => {
    const [, variables] = queryKey as [
      string,
      GetServicesHealthHistoryByBrandsVariables
    ];
    const { brands } = variables;

    const data = await getServicesHealthHistoryByBrandsService({
      brands,
      services: service ? [service] : undefined,
      from: moment().subtract(15, "days").toISOString(),
      to: moment().toISOString(),
    });

    // Transform data immutably
    // Transform data immutably
    const transformedData = {
      ...data,
      ServiceHealthHistory:
        data?.BrandsHealthHistory[0]?.ServiceHealthHistory?.map((history) => ({
          ...history,
          History: history.History?.map((item) => ({
            ...item,
            healthPercentage: Math.round(item.healthPercentage || 0), // Ensure healthPercentage is an integer
          })),
        })),
    };

    return transformedData;
  };

  const getTestsExecutions = async ({
    queryKey,
  }: {
    queryKey: [string, GetTestsExecutionsVariables];
  }) => {
    const [, variables] = queryKey as [string, GetTestsExecutionsVariables];
    const { service, date } = variables;

    return getTestsExecutionsService({
      service: service || undefined,
      date: date || undefined,
    });
  };

  const getPlatformHealthServiceDetail = async ({
    queryKey,
  }: {
    queryKey: [string, GetPlatformHealthVariables];
  }) => {
    const [, variables] = queryKey as [string, GetPlatformHealthVariables];
    const { brands, services, date } = variables;

    return getPlatformHealthService({
      brands: brands && brands.length > 0 ? brands : undefined,
      services: services || undefined,
      // format  2025-04-24T22:00:00.000Z
      date: date || selectedDate.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
    });
  };

  const fetchPlatformHealthServiceDetail = (date: moment.Moment) => {
    console.log("fetchPlatformHealthServiceDetail", date);
    getPlatformHealthServiceDetail({
      queryKey: [
        "getPlatformHealthServiceDetail",
        {
          services: service ? [service] : [],
          date: date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        },
      ],
    });
  };

  const getPlatformHealthForLastDays = async () => {
    console.log("selectedServices", service);
    if (service) {
      return []; // Return empty if services are selected
    }
    const lastDaysData = await Promise.all(
      Array.from({ length: NUMBER_LAST_DAYS }, (_, i) => {
        const date = moment().subtract(i, "days").toISOString();
        const selectedCodes = selectedBrands.map((brand) => Number(brand.code));
        return getPlatformHealthService({
          brands:
            selectedCodes && selectedCodes.length > 0
              ? selectedCodes
              : undefined,
          services: undefined,
          date,
        });
      })
    );

    return lastDaysData.reverse(); // Reverse to show oldest to newest
  };

  const { data: lastDaysHealthData, isLoading: isLoadingLastDaysHealthData, error: lastDaysHealthError } =
    useQuery(
      // Add selectedBrands to the query key so it refreshes when brands change
      ["getPlatformHealthForLastDays", selectedBrands],
      getPlatformHealthForLastDays,
      {
        cacheTime: Infinity,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        enabled: service === undefined, // Only fetch if no service is selected
        retry: 0, // Never retry
        select: (data) => {
          // Transform your data here
          return data?.map((dayData) => ({
            ...dayData,
            // Example transformation: round health percentages
            PlatformHealth:
              selectedBrands.length > 0
                ? dayData?.BrandHealth.find((brand) => {
                  const brandCode = Number(brand.Brand);
                  return selectedBrands.some(
                    (selectedBrand) =>
                      Number(selectedBrand.code) === brandCode
                  );
                })
                : dayData?.PlatformHealth,
          }));
        },
      }
    );

  const sortedLastDaysHealthData = React.useMemo(() => {
    if (!lastDaysHealthData) return [];
    return [...lastDaysHealthData].sort((a, b) => {
      const dateA = moment(a?.PlatformHealth?.Overall?.healthUpdatedAt);
      const dateB = moment(b?.PlatformHealth?.Overall?.healthUpdatedAt);
      return dateB.diff(dateA); // Sort descending by date
    });
  }, [lastDaysHealthData]);

  useEffect(() => {
    if (sortedLastDaysHealthData?.length) {
      // Buscar los datos correspondientes a la fecha seleccionada
      const selectedDayData =
        sortedLastDaysHealthData.find((dayData) => {
          const healthDate = moment(
            dayData?.PlatformHealth?.Overall?.healthUpdatedAt
          );
          return healthDate.isSame(selectedDate, "day");
        }) || sortedLastDaysHealthData[0]; // Usa el primer día como respaldo si no hay coincidencia

      const servicesToList = selectedDayData?.PlatformHealth?.Service;


      const tempColumns = [];
      if (servicesToList) {
        for (let i = 0; i < servicesToList?.length; i += servicesPerColumn) {
          tempColumns.push(servicesToList.slice(i, i + servicesPerColumn));
        }
      }
      setServicesColumns(tempColumns);
    }
  }, [sortedLastDaysHealthData, selectedDate, servicesPerColumn]); // Añadir selectedDate como dependencia

  const barChartData = {
    labels: sortedLastDaysHealthData?.map((_, i) =>
      moment().subtract(i, "days").format("DD-MM-YYYY")
    ), // Keep the labels in the original order
    datasets: [
      {
        label: "Good",
        data: sortedLastDaysHealthData?.map(
          (dayData) =>
            dayData?.PlatformHealth?.Service?.filter(
              (service) => service.health === "GOOD"
            ).length || 0
        ), // Keep the data in the original order
        backgroundColor: "#4CAF50",
      },
      {
        label: "Warning",
        data: sortedLastDaysHealthData?.map(
          (dayData) =>
            dayData?.PlatformHealth?.Service?.filter(
              (service) =>
                service.health === "WARNING" || service.health === "IMPROVABLE"
            ).length || 0
        ), // Keep the data in the original order
        backgroundColor: "#FFC107",
      },
      {
        label: "Poor",
        data: sortedLastDaysHealthData?.map(
          (dayData) =>
            dayData?.PlatformHealth?.Service?.filter(
              (service) => service.health === "POOR"
            ).length || 0
        ), // Keep the data in the original order
        backgroundColor: "#8B0000", // Dark red for "POOR"
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    onClick: (a: any, elements: any) => {
      if (elements && elements.length > 0) {
        console.log("elements", elements);
        const index = elements[0].index;
        setSelectedDate(
          moment(
            sortedLastDaysHealthData[index]?.PlatformHealth?.Overall
              ?.healthUpdatedAt
          )
        );
      }
    },
    onHover: (event: any, elements: any) => {
      const target = event.native ? event.native.target : event.target;
      if (elements.length > 0) {
        target.style.cursor = "pointer";
      } else {
        target.style.cursor = "default";
      }
    },
  };

  const { data: brandsHealthData, isLoading: isLoadingBrandsHealth, error: brandsHealthError } = useQuery(
    [
      "getPlatformHealthServiceDetail",
      {
        brands: selectedBrands?.map((brand) => Number(brand.code)) || undefined, // Convert to number[]
        services: service ? [service] : undefined,
      },
    ],
    getPlatformHealthServiceDetail,
    {
      cacheTime: 100,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: service !== undefined || selectedBrands?.length > 0,
      retry: 0, // Never retry
    }
  );

  const {
    data: serviceHealthHistory,
    isLoading: isLoadingServiceHealthHistory,
    error: serviceHealthHistoryError,
  } = useQuery(
    [
      "getServiceHealthHistory",
      {
        services: service !== undefined ? [service] : undefined,
      },
    ],
    getServicesHealthHistory,
    {
      cacheTime: 100,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: service !== undefined && selectedBrands?.length === 0,
      retry: 0, // Never retry
    }
  );

  const {
    data: serviceHealthHistoryByBrand,
    isLoading: isLoadingServiceHealthHistoryByBrand,
    error: serviceHealthHistoryByBrandError,
  } = useQuery(
    [
      "getServiceHealthHistoryByBrand",
      {
        brands: selectedBrands?.map((brand) => Number(brand.code)) || undefined, // Convert to number[]
        services: service !== undefined ? [service] : undefined,
      },
    ],
    getServicesHealthHistoryByBrands,
    {
      cacheTime: 100,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: service !== undefined && selectedBrands?.length > 0,
      retry: 0, // Never retry
    }
  );

  const { data: testsExecutions, isLoading: isLoadingTestsExecutions, error: testsExecutionsError } =
    useQuery(
      [
        "getTestsExecutions",
        {
          service: service !== undefined ? service : undefined,
          date: selectedDate.toISOString(),
        },
      ],
      getTestsExecutions,
      {
        cacheTime: 100,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        enabled: service !== undefined,
        retry: 0, // Never retry
      }
    );

  const getProjects = async () => getProjectsService();

  const { data: projectsData } = useQuery(
    ["getProjects"],
    () => getProjects(),
    {
      cacheTime: 10000,
    }
  );

  useEffect(() => {
    // Compute services
    // InBrands whenever brandsHealthData or selectedBrands changes
    if (selectedBrands?.length > 0 && brandsHealthData?.BrandHealth) {
      const computedServicesInBrands = brandsHealthData.BrandHealth.flatMap(
        (brand) => brand.Service?.map((service) => service?.service)
      );
      setServicesInBrands(computedServicesInBrands);
    } else {
      setServicesInBrands([]); // Reset if no brands are selected
    }
  }, [brandsHealthData, selectedBrands]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAddProject = (selectedProject: Project) => {
    if (selectedProject) {
      setSelectedBrands([selectedProject]); // Replace the selected brand
      setSelectedBrand(selectedProject.code);
    }
  };

  const handleDeleteProject = (projectCode: string | undefined) => {
    if (selectedBrand === projectCode) {
      setSelectedBrands([]); // Clear the selected brand
      setSelectedBrand(undefined);
    }
  };

  const handleOpenServiceDetail = (ServiceName: string) => {
    history.push(`/platformHealth/${ServiceName}`);
  };

  const handleAlarmExternalLink = (name: any) => {
    // @ts-ignore
    if (typedTarget === "PRO") {
      const url = `${HEALTH_URL}/${name}`;
      window.open(url, "_blank");
    } else {
      const url = `${HEALTH_URL}/${name}`;
      window.open(url, "_blank");
    }
  };

  const handleClickAlarmLink = React.useCallback(
    (name: string) => () => {
      handleAlarmExternalLink(name);
    },
    []
  );

  const handleBackToAllServices = () => {
    history.push("/platformHealth");
  };

  const clearAndSetAllProjects = () => {
    setSelectedBrands([]);
    setSelectedBrand(undefined);
  };

  const handleDateChange = (newDate: moment.Moment) => {
    console.log("handleDateChange", newDate);
    setSelectedDate(newDate);

    const selectedDayData = sortedLastDaysHealthData?.find((dayData) => {
      const healthDate = moment(
        dayData?.PlatformHealth?.Overall?.healthUpdatedAt
      );
      return healthDate.isSame(newDate, "day");
    });

    if (!selectedDayData) {
      console.error("No matching data found for the selected date.");
      setServicesColumns([]); // Clear services if no data is found
      return;
    }

    const servicesToList = selectedDayData?.PlatformHealth?.Service;


    const tempColumns = [];
    if (servicesToList) {
      for (let i = 0; i < servicesToList?.length; i += servicesPerColumn) {
        tempColumns.push(servicesToList.slice(i, i + servicesPerColumn));
      }
    }

    setServicesColumns(tempColumns);
    setHealthStatus(
      servicesToList?.map((service) => healthLevels[service.health]) || []
    ); // Update health status
  };

  useEffect(() => {
    if (
      !serviceHealthHistory?.ServiceHealthHistory?.[0]?.History &&
      !serviceHealthHistoryByBrand?.ServiceHealthHistory?.[0]?.History
    )
      return;
    const serviceHealthHistoryData =
      selectedBrands?.length > 0
        ? serviceHealthHistoryByBrand?.ServiceHealthHistory?.[0]?.History
        : serviceHealthHistory?.ServiceHealthHistory?.[0]?.History;
    const sortedHistory = serviceHealthHistoryData
      ? [...serviceHealthHistoryData]?.sort(
        (a: any, b: any) =>
          (a.date ? new Date(a.date).getTime() : 0) -
          (b.date ? new Date(b.date).getTime() : 0)
      )
      : [];
    setLabels(
      sortedHistory.map((item: any) =>
        item.date.split("-").reverse().join("-")
      ) || []
    );
    setHealthStatus(
      sortedHistory.map((item: any) => healthLevels[item.health]) || []
    );
    setPointColors(
      sortedHistory.map((item: any) => healthColors[item.health] || "gray") ||
      []
    );
    const datesArray = serviceHealthHistoryData?.map((history: any) =>
      moment(history.healthUpdatedAt, "YYYY-MM-DD HH:mm")
    );
    setSelectorDates(datesArray || []);
  }, [serviceHealthHistory, serviceHealthHistoryByBrand]);

  const chartData = {
    labels, // Your x-axis labels (dates)
    datasets: [
      {
        label: "Health Evolution",
        data: healthStatus, // Actual health data
        backgroundColor: pointColors,
        borderColor: "black",
        pointBackgroundColor: pointColors, // Dynamically set bullet colors
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "blue", // Add hover effects
        pointHoverBorderWidth: 32,
        pointBorderColor: pointColors, // Match border color to bullet color
        pointHoverRadius: 8,
        pointRadius: labels.map((label) =>
          moment(label, "DD-MM-YYYY").isSame(selectedDate, "day") ? 10 : 6
        ),
        fill: false,
        tension: 0.3,
      },
      {
        label: "Y-Axis Helper", // Hidden dataset for the y-axis range
        data: [0, 1, 2], // Ensure all y-axis values are present
        borderColor: "transparent", // Invisible line
        backgroundColor: "transparent",
        pointRadius: 0, // Hide points
        fill: false,
      },
    ],
  };

  const renderTestDetailOpener = React.useCallback(
    ({ onClick }: { onClick: () => void }) => (
      <button
        type="button"
        onClick={onClick}
        className="p-0 self-start text-blue-600 hover:text-blue-800 flex-shrink-0"
        aria-label="Show details for test"
      >
        <DocumentTextIcon className="h-5 w-5" />
      </button>
    ),
    []
  );

  const getHealthValue = () => {
    const dataSource =
      selectedBrands?.length > 0
        ? brandsHealthData?.BrandHealth?.find(
          (brand: { Brand: string }) => brand.Brand === selectedBrands[0].code
        )?.Service?.[0]
        : brandsHealthData?.PlatformHealth?.Service?.[0];

    return dataSource?.health;
  };

  const handleChangeProjectSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedCode = e.target.value;
      const selectedProject = projectsData?.find(
        (project: { code: any }) =>
          String(project.code) === String(selectedCode)
      );

      if (selectedProject) {
        handleAddProject(selectedProject);
        setSelectedBrand(selectedProject.code);
      } else {
        console.error("Project not found for code:", selectedCode);
      }
    },
    [projectsData, handleAddProject, setSelectedBrand]
  );

  if (!projectsData) {
    return (
      <div>
        <PageBanner title="PLATFORM HEALTH" />
        {/*
        Equivalent Tailwind classes for background, padding, rounded corners, shadow.
        Added mt-4 for spacing below the PageBanner, adjust as needed.
      */}
        <div className="bg-gray-100 p-4 rounded-lg shadow-md mt-4">
          {/* Added some basic text styling and centering for the loading message */}
          <div className="text-center text-gray-600 py-4">
            Loading projects...
          </div>
        </div>
      </div>
    );
  }

  // Refactored renderAlarmFailureDetails
  const renderAlarmFailureDetails = () => {
    const dataSource =
      selectedBrands?.length > 0
        ? brandsHealthData?.BrandHealth[0]?.Service
        : brandsHealthData?.PlatformHealth?.Service;

    console.log(
      "dataSource?.flatMap",
      dataSource?.flatMap((service: any) => service?.failureDetails)
    );
    // Use flatMap to simplify mapping and filtering, and handle potential undefined dataSource
    return (
      dataSource?.flatMap(
        (
          service: any // Use flatMap if dataSource is an array of services each having failureDetails
        ) =>
          service?.failureDetails
            ?.filter(
              (detail: { type: string; criticality: number }) =>
                detail.type === "alarm"
            ) // Filter first
            .map(
              (
                detail: {
                  name:
                  | boolean
                  | React.ReactChild
                  | React.ReactFragment
                  | React.ReactPortal
                  | string
                  | null
                  | undefined;
                  criticality: number;
                } // Then map the filtered results
              ) => (
                <div
                  key={`${service.serviceId || service.name}-${detail.name}`} // Use a more robust key if possible
                  className="flex items-start sm:items-center justify-center sm:justify-start mb-2 gap-2" // Added gap for spacing
                >
                  {/* Container for icon and text (replaces inner Box) */}
                  <div className="flex items-start flex-grow">
                    {" "}
                    {/* flex-grow to push button to the right */}
                    <ExclamationTriangleIcon
                      className="h-5 w-5 text-orange-500 self-start mr-3 flex-shrink-0 mt-0.5"
                    />
                    <span className="text-base">{detail.name}</span>
                  </div>

                  <button
                    type="button"
                    onClick={handleClickAlarmLink(detail.name as string)}
                    className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none flex-shrink-0" // Minimal button styling
                    aria-label={`Open details for alarm ${detail.name}`}
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                  </button>
                </div>
              )
            ) ?? [] // Return empty array if dataSource or failureDetails is null/undefined
      ) ?? []
    ); // Return empty array if dataSource is null/undefined
  };

  // Refactored renderTestFailureDetails
  const renderTestFailureDetails = () => {
    const filteredTestsExecutions =
      selectedBrands?.length > 0
        ? testsExecutions?.filter((detail: any) =>
          selectedBrands.some((brand) => detail.brand === brand.code)
        )
        : testsExecutions;

    return (
      filteredTestsExecutions?.filter((detail: any) => detail.criticality > 0)
        .map((detail: any) => (
          <div
            key={detail.id || detail.title} // Use a stable id if available, otherwise title
            className="mb-4 flex items-start gap-3" // Added gap-3 (12px) for spacing
          >
            <XCircleIcon
              className="h-5 w-5 text-red-500 self-start flex-shrink-0 mt-0.5" // Adjusted size, added flex-shrink-0
            />
            <span className="text-base flex-grow">{detail.title}</span>{" "}
            {/* Added flex-grow */}
            {/* TestDetailPopup remains, but its renderOpener content is refactored */}
            <TestDetailPopup
              testDetail={detail}
              renderOpener={renderTestDetailOpener}
            />
          </div>
        )) ?? []
    ); // Return empty array if filteredTestsExecutions is null/undefined
  };

  const renderAlarmFailureDetailsCount = () => {
    let dataSource: any = [];

    if (selectedBrands?.length > 0) {
      dataSource = brandsHealthData?.BrandHealth || [];
    } else if (brandsHealthData?.PlatformHealth?.Service) {
      dataSource = brandsHealthData.PlatformHealth.Service;
    }

    const result = dataSource?.map((service: any) => {
      const failureDetails =
        service?.Service?.[0]?.failureDetails || service?.failureDetails;

      if (!failureDetails) {
        return false;
      }

      return failureDetails.some(
        (detail: { type: string; criticality: number }) =>
          detail.type === "alarm"
      );
    });

    return result?.filter(Boolean) || [];
  };

  const renderTestFailureDetailsCount = () => {
    return selectedBrands?.length > 0
      ? testsExecutions?.filter((detail: any) => detail.criticality > 0)
        .filter((detail: any) =>
          selectedBrands.some((brand) => detail.brand === brand.code)
        )
      : testsExecutions?.filter((detail: any) => detail.criticality > 0);
  };

  return (
    <div>
      <PageBanner
        title={
          service !== undefined
            ? `PLATFORM HEALTH/${service}`
            : "PLATFORM HEALTH"
        }
      />
      <div>
        {/* Left Section */}
        <div>
          <div className="pl-4 pr-12 flex flex-row justify-between items-center w-full">
            <div>
              <p className="text-gray-700 mt-4 mb-4">
                Check the platform services status.
              </p>
              <div className="flex flex-col items-stretch">
                <div className="flex items-end gap-2">
                  <ProjectSelector
                    selectedBrand={selectedBrand}
                    selectedBrands={selectedBrands}
                    projectsData={projectsData}
                    onChangeProject={handleChangeProjectSelect}
                    onClearProjects={clearAndSetAllProjects}
                  />
                  <button
                    type="button"
                    onClick={() => triggerTestsExecutions.mutate()}
                    className="py-2 bg-gray-500 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-opacity-50 px-2"
                    disabled={triggerTestsExecutions.isLoading}
                  >
                    {triggerTestsExecutions.isLoading
                      ? "Triggering..."
                      : "Trigger Tests"}
                  </button>
                </div>
                <SelectedBrandsChips
                  selectedBrands={selectedBrands}
                  onDelete={handleDeleteProject}
                />
              </div>
            </div>

            <div className="mt-4">
              {!isLoadingLastDaysHealthData && !service && (
                <div className="flex flex-col mb-4">
                  <label htmlFor="day-selector" className="text-gray-700 mb-4">
                    Health on:
                  </label>
                  <select
                    id="day-selector"
                    className="p-2 border rounded-md"
                    value={selectedDate.format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const newDate = moment
                        .utc(e.target.value, "YYYY-MM-DD")
                        .local();
                      handleDateChange(newDate);
                    }}
                  >
                    {sortedLastDaysHealthData?.map((dayData) => {
                      const healthUpdatedAt =
                        dayData?.PlatformHealth?.Overall?.healthUpdatedAt;
                      const formattedHealthUpdatedAt = moment
                        .utc(healthUpdatedAt)
                        .local()
                        .format("YYYY-MM-DD HH:mm:ss");
                      const labelHealthUpdatedAt =
                        moment(healthUpdatedAt).format("YYYY-MM-DD");
                      return (
                        <option
                          key={labelHealthUpdatedAt}
                          value={labelHealthUpdatedAt}
                        >
                          {formattedHealthUpdatedAt}
                        </option>
                      );
                    })}
                  </select>
                  <div className="text-sm text-gray-500 mt-2">
                    Last test execution:{" "}
                    {moment(localStorage.getItem("lastTestExecution"))
                      .format("YYYY-MM-DD HH:mm:ss")
                      .toString()}
                  </div>
                </div>
              )}
            </div>

            <div>
              {service && (
                // here the status badge of the selected service bigger
                <div className="flex items-center">
                  {getHealthIcon({
                    health: getHealthValue(),
                    size: "h-16 w-16", // Larger size for the icon
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State, Error State or Content */}
      <div className="mt-6">
        {/* Error States */}
        {(!service && lastDaysHealthError) ? (
          <ErrorMessage 
            error={lastDaysHealthError} 
            onRetry={() => window.location.reload()} 
          />
        ) : (selectedBrands?.length > 0 && brandsHealthError) ? (
          <ErrorMessage 
            error={brandsHealthError} 
            onRetry={() => window.location.reload()} 
          />
        ) : /* Loading States */
        (!service && isLoadingLastDaysHealthData) ||
          (selectedBrands?.length > 0 && isLoadingBrandsHealth) ? (
          <LoadingAnimation title="Loading platform health" />
        ) : (
          <div className="m-4">
            {/* All Services View */}
            {!service && (
              <div className="flex flex-col md:flex-row justify-between mt-4 gap-4">
                <div className="mt-4">
                  <AllServicesList
                    servicesColumns={servicesColumns}
                    getHealthIcon={getHealthIcon}
                    handleOpenServiceDetail={handleOpenServiceDetail}
                  />
                </div>
                <div className="flex flex-col items-center mt-8 mr-24">
                  <ServicesHealthSummary
                    platformHealth={
                      sortedLastDaysHealthData?.find((dayData) => {
                        const healthDate = moment(
                          dayData?.PlatformHealth?.Overall?.healthUpdatedAt
                        );
                        return healthDate.isSame(selectedDate, "day");
                      })?.PlatformHealth
                    }
                  />
                  <div className="mt-8 w-full">
                    <Bar data={barChartData} options={barChartOptions} />
                  </div>
                </div>
              </div>
            )}

            {/* Single Service Detail View */}
            {service !== undefined && (
              <>
                {/* Error handling for service detail view */}
                {(serviceHealthHistoryError || serviceHealthHistoryByBrandError || testsExecutionsError) ? (
                  <ErrorMessage 
                    error={serviceHealthHistoryError || serviceHealthHistoryByBrandError || testsExecutionsError}
                    onRetry={() => window.location.reload()} 
                  />
                ) : (
                  <SingleServiceDetailView
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectorDates={selectorDates}
                    labels={labels}
                    chartData={chartData}
                    options={options}
                    selectedBrands={selectedBrands}
                    renderAlarmFailureDetails={renderAlarmFailureDetails}
                    renderAlarmFailureDetailsCount={renderAlarmFailureDetailsCount}
                    renderTestFailureDetails={renderTestFailureDetails}
                    renderTestFailureDetailsCount={renderTestFailureDetailsCount}
                    handleBackToAllServices={handleBackToAllServices}
                    isLoadingServiceHealthHistory={isLoadingServiceHealthHistory}
                    isLoadingServiceHealthHistoryByBrand={
                      isLoadingServiceHealthHistoryByBrand
                    }
                    isLoadingTestsExecutions={isLoadingTestsExecutions}
                    fetchPlatformHealthServiceDetail={
                      fetchPlatformHealthServiceDetail
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
